import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { processReq } from "../../helpers/processRequest";
import { GET, POST, PUT } from "../../utils/requestActionConstants";
import { API_ROUTE } from "../../utils/commonConstants";
import dayjs from "dayjs";

const init = {
    inventory: {},
    inventoryList: [],
    loading: {},
    error: null
};

export const inventorySlice = createSlice({
    name: "inventorySlice",
    initialState: init,
    reducers: {
        setInventory: (state, action) => {
            let inventory = action.payload;

            state.inventory = inventory;
        },

        setInventoriesList: (state, action) => {
            state.inventoryList = action.payload;
        },

        clearData: (state) => {
            return init;
        },
    },
    extraReducers: (builder) => {
        builder
        // Fetch inventory list
        .addCase(fetchInventoryList.pending, (state) => {
            state.loading.fetching = true;
            state.error = null;
        })
        .addCase(fetchInventoryList.fulfilled, (state, action) => {
            state.loading.fetching = false;
            state.inventoryList = action.payload;
        })
        .addCase(fetchInventoryList.rejected, (state, action) => {
            state.loading.fetching = false;
            state.error = action.payload;
        })

        // Add inventory
        .addCase(addInventory.pending, (state) => {
            state.loading.saving = true;
            state.error = null;
        })
        .addCase(addInventory.fulfilled, (state, action) => {
            state.loading.saving = false;
            state.inventory = action.payload;
        })
        .addCase(addInventory.rejected, (state, action) => {
            state.loading.saving = false;
            state.error = action.payload;
        })

        // Update inventory
        .addCase(updateInventory.pending, (state) => {
            state.loading.saving = true;
            state.error = null;
        })
        .addCase(updateInventory.fulfilled, (state, action) => {
            state.loading.saving = false;
            state.inventory = action.payload;
        })
        .addCase(updateInventory.rejected, (state, action) => {
            state.loading.saving = false;
            state.error = action.payload;
        });
    }
});

export const fetchInventoryList = createAsyncThunk(
    "inventorySlice/fetchInventoryList",
    async ({productId}, { rejectWithValue }) => {
        try {
            const response = await processReq(GET, `${API_ROUTE}/inventories?productId=${productId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch inventory list");
        }
    }
);

export const updateInventory = createAsyncThunk(
    "inventorySlice/updateInventory",
    async ({ inventoryId, inventoryData }, { rejectWithValue, dispatch }) => {
        try {
            const response = await processReq(PUT, `${API_ROUTE}/inventories/${inventoryId}`, inventoryData);

            dispatch(fetchInventoryList({productId: response.data.productId}));

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to update inventory");
        }
    }
);

export const addInventory = createAsyncThunk(
    "inventorySlice/addInventory",
    async ({ inventoryData }, { rejectWithValue, dispatch }) => {
        try {
            const response = await processReq(POST, `${API_ROUTE}/inventories`, inventoryData);
            
            dispatch(fetchInventoryList({productId: response.data.productId}));

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to add inventory");
        }
    }
);

const selectInventory = (state) => state.inventorySlice.inventory;

export const getInventory = createSelector(
  [selectInventory],
  (inventory) => {
    return {
        ...inventory,
        expiryDate: inventory.expiryDate ? dayjs(inventory.expiryDate) : '',
        manufacturedDate: inventory.manufacturedDate ? dayjs(inventory.manufacturedDate) : '',
    };
  }
);

export const { clearData, setInventory, setInventoriesList } = inventorySlice.actions;

export default inventorySlice.reducer;
